import React from "react";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>
          Privacy Policy - Trademark Prime Unit | Data Protection & Privacy
        </title>
        <meta
          name="description"
          content="Trademark Prime Unit Privacy Policy. Learn how we collect, use, and protect your personal information. Committed to transparency and data security."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-8">
            Privacy Policy
          </h1>
          <p className="text-gray-600 mb-12">Last updated: December 2025</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p>
                Trademark Prime Unit ("we," "our," or "us") is committed to
                protecting your privacy. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your information when you
                visit our website and use our services.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                2. Information We Collect
              </h2>
              <p className="mb-3">
                We may collect information about you in a variety of ways,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Personal Data: Name, email address, phone number, and other
                  contact information you voluntarily provide
                </li>
                <li>
                  Business Information: Company name, trademark details, and
                  related business information
                </li>
                <li>
                  Usage Data: Information about how you interact with our
                  website and services
                </li>
                <li>
                  Technical Data: IP address, browser type, device information,
                  and operating system
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="mb-3">
                We use the collected information for various purposes,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Providing and maintaining our trademark registration services
                </li>
                <li>
                  Processing your trademark applications and related requests
                </li>
                <li>Communicating with you about your case and our services</li>
                <li>Improving our website and customer service</li>
                <li>
                  Sending you updates, marketing communications, and promotional
                  materials (with your consent)
                </li>
                <li>
                  Complying with legal obligations and protecting our legal
                  rights
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="mb-3">
                We may share your information in the following situations:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  With government agencies (USPTO) as required for trademark
                  registration
                </li>
                <li>
                  With service providers who assist us in operating our business
                </li>
                <li>When required by law or to protect our rights</li>
                <li>In connection with a business transfer or merger</li>
              </ul>
              <p className="mt-3">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                5. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational security
                measures to protect your personal information. However, no
                method of transmission over the Internet or electronic storage
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                6. Your Rights and Choices
              </h2>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access and receive a copy of your personal information</li>
                <li>
                  Request correction of inaccurate or incomplete information
                </li>
                <li>
                  Request deletion of your personal information (subject to
                  legal obligations)
                </li>
                <li>Opt-out of marketing communications</li>
                <li>
                  Withdraw consent where we rely on consent to process your
                  information
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                7. Cookies and Tracking Technologies
              </h2>
              <p>
                We use cookies and similar tracking technologies to track
                activity on our website and hold certain information. You can
                instruct your browser to refuse all cookies or to indicate when
                a cookie is being sent.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                8. Third-Party Links
              </h2>
              <p>
                Our website may contain links to third-party websites. We are
                not responsible for the privacy practices or content of these
                external sites. We encourage you to review their privacy
                policies.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                9. Children's Privacy
              </h2>
              <p>
                Our services are not directed to individuals under the age of
                18. We do not knowingly collect personal information from
                children under 18.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                10. Refund Policy
              </h2>
              <p>
                By purchasing any of our trademark search, clearance, filing, or
                related legal support services, you acknowledge and agree to the
                following refund policy:
              </p>
              <p>
                <b>1. Service Fees</b>
                <br />
                Our service fees cover professional time, attorney review,
                administrative processing, legal research, and preparation of
                your trademark matter. These fees are earned as services are
                performed.
              </p>
              <p>
                <b>2. Refund Requests Before Work Begins</b>
                <br />
                If you request to cancel your order immediately after purchase
                and **before any substantive work has commenced**, including but
                not limited to attorney assignment, trademark search, legal
                review, or preparation of your clearance report, you may be
                eligible for a **partial refund**. Any refund issued will be
                subject to deduction of applicable payment processing fees,
                administrative costs, and any work already performed.
              </p>
              <p>
                <b>3. Non-Refundable After Clearance Report</b>
                <br />
                Once your attorney-conducted trademark search and clearance
                report has been completed and delivered to you, the **service
                fee becomes fully earned and is non-refundable**, regardless of
                whether you decide to proceed with filing, respond to our
                communications, or discontinue the process.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <p>
                We may update our Privacy Policy from time to time. We will
                notify you of any changes by posting the new Privacy Policy on
                this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                12. Contact Us
              </h2>
              <p className="mb-3">
                If you have questions about this Privacy Policy, please contact
                us:
              </p>
              <ul className="list-none space-y-2 ml-4">
                <li>
                  Phone:{" "}
                  <a href="tel:+18776851678" className="hover:underline">
                    +1 (877) 685-1678
                  </a>
                </li>
                <li>
                  Email:{" "}
                  <a
                    href="mailto:info@trademarkprimeunit.com"
                    className="hover:underline"
                  >
                    info@trademarkprimeunit.com
                  </a>
                </li>
                <li>
                  Address: Two California Plaza 350 Grand Lower, Los Angeles, CA
                  90071
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
